import React, { useEffect, useRef, useState } from "react";
import FormUI from '@/componentsUI/Form';
import { feedback } from "@/utils/feedback";
import { useTranslationPlus } from "@/Core/i18n/useTranslationPlus";
import {
  useWalletAll,
  useWalletWithdrawFlat,
  useWithdrawFlat,
} from "@/store/walletStore";
import { useUserInfo } from "@/store/userInfo";
import FromItemPlus from "@/components/FormItemPlus/FromItemPlus";
import WidthdrawCurrency from "../compoments/widthdrawCurrency/WidthdrawCurrency";
import LoadingUI from "@/componentsUI/LoadingUI/LoadingUI";
import { IFInitWalletWithdrawFlatReq } from "@/api/apiReq/IFWalletReq";
import { sys } from "@/Core/utils/system";
import WidthdrawPix from "../pix/WidthdrawPix";
import {
  IFInitWalletRsp,
  IFInitWalletWithdrawFlatRsp,
} from "@/api/apiRsp/IFWalletRsp";
import ButtonUI from "@/componentsUI/Button";
import "../index.less";
import useDialogManager from "@/utils/useDialogManager";
import {
  AmountMultiplierConversion,
  dealAmount9Valid,
} from "@/Core/utils/currencyDealCenter";
import useDialog from "@/utils/useDialog";
import InternalWithdrawWayField from "../compoments/widthdrawWay/InternalWithdrawWayField";
import BankSelector from "../compoments/bankSelector/BankSelector";
import BankAccountInput from "../compoments/bankAccountInput/BankAccountInput";
import MexicoBankCode102 from "../assets/mx/Bankcode-102.json";
import { getWithdrawalMobile } from "@/utils/mobileUtils";
import InputUI from "@/componentsUI/Input";
import MexicoConfirmModal from "@/pages/wallet/shared/MexicoConfirmModal/MexicoConfirmModal";
import {
  getWithdrawBankInfo,
  saveWithdrawBankInfo,
} from "../withdrawStorage";
import { runWithdrawalOfficialAppPrompt } from "@/features/install/withdrawalOfficialAppPrompt";

const FlatWithdrawMexico = () => {
  const [form] = FormUI.useForm();
  const { t } = useTranslationPlus();
  const { data: wallets } = useWalletAll();
  const { data: userInfo } = useUserInfo();
  const formValidateRef = useRef(null);
  const [params, setParams] = useState<IFInitWalletWithdrawFlatReq>(
    wallets?.flat
      ? {
          currencyType:
            wallets?.flat[0]?.serverCurrencyType ||
            wallets?.flat[0]?.currencyType,
          device: sys.device,
        }
      : null,
  );
  const { data, isValidating, error, mutate } = useWithdrawFlat(params);
  const [currentWallet, setCurrentWallet] = useState<IFInitWalletRsp>(
    wallets?.flat?.[0],
  );

  const [widthdrawConfig, setWidthdrawConfig] =
    useState<IFInitWalletWithdrawFlatRsp>();

  const [selectedBank, setSelectedBank] = useState(null);
  const [accountNumber, setAccountNumber] = useState("");
  const selectedWithdrawType = 102; // 固定为 102
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingWithdrawValues, setPendingWithdrawValues] = useState(null);

  useEffect(() => {
    const bankInfo = getWithdrawBankInfo(userInfo?.userId);
    if (
      bankInfo?.bank &&
      bankInfo.accountNumber &&
      bankInfo.withdrawType === selectedWithdrawType
    ) {
      setSelectedBank(bankInfo.bank);
      setAccountNumber(bankInfo.accountNumber);
      form.setFieldValue("bankCode", bankInfo.bank.code);
      form.setFieldValue("accountNumber", bankInfo.accountNumber);
    } else {
      setSelectedBank(null);
      setAccountNumber("");
      form.setFieldValue("bankCode", undefined);
      form.setFieldValue("accountNumber", "");
    }
  }, [form, selectedWithdrawType, userInfo?.userId]);

  function changeWidthdraw(index) {
    const item = wallets.flat?.[index];
    if (!item) {
      return;
    }
    setParams({
      currencyType: item.serverCurrencyType || item.currencyType,
      device: sys.device,
    });
    setCurrentWallet(item);
  }

  useEffect(() => {
    if (wallets?.flat?.length) {
      form.setFieldValue("walletType", wallets?.flat?.[0]?.currencyType);
    }
  }, [wallets]);

  useEffect(() => {
    form.setFieldValue(
      "mobile",
      getWithdrawalMobile(userInfo?.mobile, userInfo?.userId)
    );
  }, [userInfo]);

  const { trigger, isMutating } = useWalletWithdrawFlat();
  const { openGoogleTowFactor } = useDialogManager();
  const { closeAll } = useDialog();

  const onClickWidthdraw = () => {
    if (!selectedBank) {
      feedback.error(t("Wallet.Please select a bank"));
      return;
    }

    if (!accountNumber) {
      feedback.error(t("Wallet.Please enter account number"));
      return;
    }

    formValidateRef.current = true;
    form
      .validate()
      .then((v) =>
        runWithdrawalOfficialAppPrompt({
          stage: "before_submit",
          userId: userInfo?.userId,
          onContinue: () => {
            setPendingWithdrawValues(v);
            setShowConfirmModal(true);
          },
        }),
      )
      .catch(() => undefined)
      .finally(() => {
        formValidateRef.current = false;
      });
  };

  const handleWithdrawConfirm = () => {
    if (!pendingWithdrawValues) return;

    const v = pendingWithdrawValues;
    const selectedWallet = wallets?.flat?.find(
      (wallet) => wallet.currencyType === v.walletType,
    );
    const requestParams = {
      currencyType: selectedWallet?.serverCurrencyType || v.walletType,
      money: AmountMultiplierConversion(Math.floor(v.amount), {
        currencyType: selectedWallet?.currencyType,
        serverCurrencyType: selectedWallet?.serverCurrencyType,
      }),
      withdrawType: selectedWithdrawType,
      accountInfo: JSON.stringify({
        pixFullName: v.fullName,
        bankCode: selectedBank.code,
        bankAccount: accountNumber,
      }),
      accountType: selectedBank.code,
      accountNumber: accountNumber,
      mobile: form.getFieldValue("mobile") || "",
      withdrawMobileUserId: userInfo?.userId,
    };

    trigger(requestParams)
      .then((res) => {
        setShowConfirmModal(false);
        setPendingWithdrawValues(null);

        if (res?.googleAuth) {
          openGoogleTowFactor({
            trigger: trigger,
            params: requestParams,
            complete: () => {
              const bankInfo = {
                bank: selectedBank,
                accountNumber: accountNumber,
                withdrawType: selectedWithdrawType,
              };
              saveWithdrawBankInfo(bankInfo, userInfo?.userId);
              closeAll();
            },
          });
        } else {
          const bankInfo = {
            bank: selectedBank,
            accountNumber: accountNumber,
            withdrawType: selectedWithdrawType,
          };
          saveWithdrawBankInfo(bankInfo, userInfo?.userId);
          closeAll();
        }
      })
      .catch((e) => {
        setShowConfirmModal(false);
        setPendingWithdrawValues(null);

        if (e?.code == "ERROR_60028") {
          if (e?.data?.withdrawAmount == 0) {
            feedback.error(
              t("WITHDRAW_NEED_BET", {
                betCode: dealAmount9Valid(e?.data?.betTarget, {
                  currencyType: e?.data?.currency,
                }),
              }),
            );
          } else {
            feedback.error(
              t("WITHDRAW_ALREADY_BET", {
                alreadyCode: dealAmount9Valid(e?.data?.withdrawAmount, {
                  currencyType: e?.data?.currency,
                }),
              }),
            );
          }
        } else {
          feedback.error(t(e?.code));
        }
      });
  };

  const handleConfirmModalClose = () => {
    setShowConfirmModal(false);
    setPendingWithdrawValues(null);
  };

  function changeType({ data }) {
    setWidthdrawConfig(data);
  }

  useEffect(() => {
    if (data && data?.configParams) {
      const configFor102 =
        data.configParams.find((config) => config.withdrawType === 102) ||
        data.configParams[0];

      form.setFieldValue("witdrawWay", {
        type: configFor102.withdrawType,
        data: configFor102,
      });
      setWidthdrawConfig(configFor102);
    }
  }, [data]);

  const handleBankSelect = (bank) => {
    setSelectedBank(bank);
    if (bank) {
      form.setFieldValue("bankCode", bank.code);
    }
  };

  const handleAccountNumberChange = (value) => {
    setAccountNumber(value);
    form.setFieldValue("accountNumber", value);
  };

  return (
    <div className="flat-widthdraw">
      <FormUI form={form} layout={"vertical"} autoComplete={"off"}>
        <FromItemPlus
          field={"walletType"}
          label={t("Wallet.WithdrawCurrencyTitle")}
        >
          <WidthdrawCurrency
            currencys={wallets?.flat}
            onChange={(_value: any, option: any) => {
              changeWidthdraw(option?._index);
            }}
          />
        </FromItemPlus>

        <LoadingUI
          isLoading={isValidating}
          error={error}
          dataList={data}
          retryFunc={mutate}
          className="wallet-flat-loading-state"
        >
          <>
            <InternalWithdrawWayField
              configs={data?.configParams}
              onChange={changeType}
            />

            <div className="withdraw-bank-section">
              <div className="bank-selector-wrapper">
                <label className="bank-selector-label">
                  <span className="withdraw-required-mark">*</span>
                  {t("Wallet.Select Bank")}
                </label>
                <BankSelector
                  value={selectedBank}
                  onChange={handleBankSelect}
                  placeholder={t("Wallet.Select Bank")}
                  withdrawType={selectedWithdrawType}
                  availableChannels={[102]}
                  bankCodeDataByChannel={{
                    102: MexicoBankCode102,
                  }}
                />
              </div>

              <FromItemPlus
                field={"accountNumber"}
                label={t("Wallet.Bank Card Number")}
                rules={[
                  {
                    required: true,
                    message: t("Wallet.Please enter bank card number"),
                  },
                  {
                    validator: (value, cb) => {
                      if (!value) {
                        return cb();
                      }
                      const numericValue = value.replace(/\D/g, "");
                      if (
                        numericValue.length !== 16 &&
                        numericValue.length !== 18
                      ) {
                        return cb(
                          t("Wallet.Bank card number must be 16 or 18 digits"),
                        );
                      }
                      return cb();
                    },
                  },
                ]}
              >
                <BankAccountInput
                  value={accountNumber}
                  onChange={handleAccountNumberChange}
                  placeholder={t("Wallet.Please enter bank card number")}
                />
              </FromItemPlus>

              <FromItemPlus
                field={"fullName"}
                label={t("Wallet.Full Name")}
                rules={[
                  {
                    required: true,
                    message: t("Wallet.Please enter your full name"),
                  },
                  {
                    minLength: 2,
                    message: t(
                      "Wallet.Full name must be at least 2 characters",
                    ),
                  },
                ]}
              >
                <InputUI
                  placeholder={t("Wallet.Please enter your full name")}
                  maxLength={100}
                />
              </FromItemPlus>
            </div>

            <WidthdrawPix
              data={data}
              widthdrawConfig={widthdrawConfig}
              currentWallet={currentWallet}
              form={form}
              formValidateRef={formValidateRef}
            />
          </>
        </LoadingUI>
      </FormUI>

      <ButtonUI
        className={`widthdraw-btn`}
        type="primary"
        onClick={onClickWidthdraw}
        disabled={isMutating}
      >
        {t("Wallet.Withdraw")}
      </ButtonUI>

      <MexicoConfirmModal
        visible={showConfirmModal}
        onClose={handleConfirmModalClose}
        onConfirm={handleWithdrawConfirm}
        type="withdraw"
        loading={isMutating}
        autoTriggerSeconds={5}
      />
    </div>
  );
};

export default FlatWithdrawMexico;
